/**
 * CORNER_V2: Slot Inspector Panel — shows detailed metadata for a signing slot
 * in configure mode. Allows inline edits (party, label, type) and shows
 * detection provenance (confidence, source, partyHint).
 */
import { useState, useEffect, useCallback } from 'react';
import type { FieldDescriptor } from './signingTypes';
import { PDF_VIEWER } from './pdfViewerTokens';
import { APP } from '../app/tokens';

export interface SlotUpdate {
  partyId?: string;
  label?: string;
  type?: 'signature' | 'initials' | 'date' | 'text';
  rect?: { x: number; y: number; width: number; height: number };
}

export interface Party {
  id: string;
  label: string;
}

export interface SlotInspectorPanelProps {
  slot: FieldDescriptor | null;
  parties: Party[];
  onClose: () => void;
  onUpdate: (slotId: string, updates: SlotUpdate) => void;
}

const FIELD_TYPES = ['signature', 'initials', 'date', 'text'] as const;

function confidenceColor(c: number): string {
  if (c >= 0.8) return PDF_VIEWER.fieldSigned;
  if (c >= 0.5) return '#E6A817';
  return '#DC4A3A';
}

function truncateId(id?: string): string {
  if (!id) return '—';
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '5px 8px', fontSize: 12, borderRadius: 6,
  border: `1px solid ${APP.border}`, backgroundColor: APP.bgContent,
  color: APP.textPrimary, boxSizing: 'border-box', outline: 'none',
};

const selectStyle: React.CSSProperties = { ...inputStyle, cursor: 'pointer' };

const labelStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 600, color: APP.textMuted,
  textTransform: 'uppercase', letterSpacing: '0.04em',
  marginBottom: 3,
};

const rowStyle: React.CSSProperties = { marginBottom: 12 };

export default function SlotInspectorPanel({ slot, parties, onClose, onUpdate }: SlotInspectorPanelProps) {
  const [editType, setEditType] = useState<string>(slot?.type ?? 'signature');
  const [editLabel, setEditLabel] = useState(slot?.label ?? '');
  const [editPartyId, setEditPartyId] = useState(slot?.partyId ?? '');

  // Reset when slot changes
  useEffect(() => {
    if (slot) {
      setEditType(slot.type);
      setEditLabel(slot.label);
      setEditPartyId(slot.partyId);
    }
  }, [slot]);

  const handleSave = useCallback(() => {
    if (!slot) return;
    const updates: SlotUpdate = {};
    if (editType !== slot.type) updates.type = editType as SlotUpdate['type'];
    if (editLabel !== slot.label) updates.label = editLabel;
    if (editPartyId !== slot.partyId) updates.partyId = editPartyId;
    if (Object.keys(updates).length > 0) onUpdate(slot.id, updates);
    onClose();
  }, [slot, editType, editLabel, editPartyId, onUpdate, onClose]);

  // Keyboard: Escape closes
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!slot) {
    return (
      <div style={{
        width: 280, flexShrink: 0, padding: 16,
        borderLeft: `1px solid ${APP.border}`, backgroundColor: APP.bgSidebar,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: APP.textMuted, fontSize: 12, textAlign: 'center',
      }}>
        Click a field to inspect its properties.
      </div>
    );
  }

  const conf = slot.confidence;

  return (
    <div style={{
      width: 280, flexShrink: 0, overflowY: 'auto',
      borderLeft: `1px solid ${APP.border}`, backgroundColor: APP.bgSidebar,
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 14px', borderBottom: `1px solid ${APP.border}`,
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: APP.textPrimary }}>Field Inspector</span>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: APP.textMuted, fontSize: 16, padding: 0, lineHeight: 1 }}
        >
          ×
        </button>
      </div>

      {/* Body */}
      <div style={{ padding: 14, flex: 1 }}>
        {/* ── Editable Fields ────────────────────── */}
        <div style={rowStyle}>
          <div style={labelStyle}>Type</div>
          <select value={editType} onChange={(e) => setEditType(e.target.value)} style={selectStyle}>
            {FIELD_TYPES.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
          </select>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Label</div>
          <input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} style={inputStyle} />
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Assigned Party</div>
          <select value={editPartyId} onChange={(e) => setEditPartyId(e.target.value)} style={selectStyle}>
            {parties.length > 0 ? (
              parties.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)
            ) : (
              <>
                <option value="self">Self</option>
                <option value="other">Other</option>
                {editPartyId && editPartyId !== 'self' && editPartyId !== 'other' && (
                  <option value={editPartyId}>{editPartyId}</option>
                )}
              </>
            )}
          </select>
        </div>

        {slot.role && (
          <div style={rowStyle}>
            <div style={labelStyle}>Role</div>
            <div style={{ fontSize: 12, color: APP.textSecondary, fontFamily: 'monospace' }}>{slot.role}</div>
          </div>
        )}

        <div style={rowStyle}>
          <div style={labelStyle}>Status</div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
            backgroundColor: slot.signed ? `${PDF_VIEWER.fieldSigned}22` : `${PDF_VIEWER.fieldUnsigned}22`,
            color: slot.signed ? PDF_VIEWER.fieldSigned : PDF_VIEWER.fieldUnsigned,
          }}>
            {slot.signed ? '● Signed' : '○ Pending'}
          </div>
        </div>

        {/* ── Divider ────────────────────────────── */}
        <div style={{ height: 1, backgroundColor: APP.border, margin: '8px 0 12px' }} />

        {/* ── Source & Detection ──────────────────── */}
        <div style={{ fontSize: 11, fontWeight: 600, color: APP.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Detection
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Source</div>
          <div style={{ fontSize: 12, color: APP.textSecondary }}>
            {slot.source.kind === 'slot' ? 'Signing Slot' : slot.source.kind === 'field' ? 'Signature Field' : 'Unknown'}
          </div>
        </div>

        {slot.source.slotId && (
          <div style={rowStyle}>
            <div style={labelStyle}>Slot ID</div>
            <div style={{ fontSize: 11, color: APP.textMuted, fontFamily: 'monospace' }}>{truncateId(slot.source.slotId)}</div>
          </div>
        )}
        {slot.source.fieldId && (
          <div style={rowStyle}>
            <div style={labelStyle}>Field ID</div>
            <div style={{ fontSize: 11, color: APP.textMuted, fontFamily: 'monospace' }}>{truncateId(slot.source.fieldId)}</div>
          </div>
        )}

        {conf != null && (
          <div style={rowStyle}>
            <div style={labelStyle}>Confidence</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ flex: 1, height: 6, borderRadius: 3, backgroundColor: `${APP.border}` }}>
                <div style={{ width: `${conf * 100}%`, height: '100%', borderRadius: 3, backgroundColor: confidenceColor(conf), transition: 'width 0.2s' }} />
              </div>
              <span style={{ fontSize: 11, fontWeight: 600, color: confidenceColor(conf) }}>{Math.round(conf * 100)}%</span>
            </div>
          </div>
        )}

        {slot.partyHint && (
          <div style={rowStyle}>
            <div style={labelStyle}>Party Hint</div>
            <span style={{
              display: 'inline-block', fontSize: 10, padding: '2px 6px', borderRadius: 4,
              backgroundColor: `${PDF_VIEWER.fieldUnsigned}22`, color: PDF_VIEWER.fieldUnsigned,
            }}>
              {slot.partyHint}
            </span>
          </div>
        )}

        {/* ── Divider ────────────────────────────── */}
        <div style={{ height: 1, backgroundColor: APP.border, margin: '8px 0 12px' }} />

        {/* ── Geometry ───────────────────────────── */}
        <div style={{ fontSize: 11, fontWeight: 600, color: APP.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Position
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {(['x', 'y', 'width', 'height'] as const).map((key) => (
            <div key={key}>
              <div style={{ ...labelStyle, fontSize: 9 }}>{key.toUpperCase()}</div>
              <div style={{ fontSize: 11, color: APP.textSecondary, fontFamily: 'monospace' }}>
                {slot.rect[key].toFixed(1)}%
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 6 }}>
          <div style={{ ...labelStyle, fontSize: 9 }}>PAGE</div>
          <div style={{ fontSize: 11, color: APP.textSecondary }}>{slot.page}</div>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        padding: '10px 14px', borderTop: `1px solid ${APP.border}`,
        display: 'flex', gap: 8,
      }}>
        <button
          onClick={onClose}
          style={{
            flex: 1, padding: '6px 0', borderRadius: 6, border: `1px solid ${APP.border}`,
            background: 'transparent', color: APP.textSecondary, fontSize: 12, cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          style={{
            flex: 1, padding: '6px 0', borderRadius: 6, border: 'none',
            background: PDF_VIEWER.fieldUnsigned, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Save
        </button>
      </div>
    </div>
  );
}
